import { getReportsPage, getReportDetail } from './reportApi';
import { getRealizedTradePage, RealizedTrade } from './historyApi';
import { DrawdownPoint, EquityPoint, MonthlyReturn } from '../types/performance';
import { Position } from './reportApi';

export interface OperatingPerformanceData {
  range: { start: string; end: string; days: number };
  summary: {
    totalValue: number;
    cash: number;
    equity: number;
    totalReturn: number;
    latestDailyReturn: number;
    maxDrawdown: number;
    realizedProfit: number;
    unrealizedProfit: number;
    winRate: number;
    tradeCount: number;
    openPositions: number;
  };
  tradeBreakdown: Array<{ type: string; count: number; profit: number }>;
  equityCurve: EquityPoint[];
  drawdownSeries: DrawdownPoint[];
  monthlyReturns: MonthlyReturn[];
  positions: Position[];
  trades: RealizedTrade[];
}

export async function fetchOperatingPerformance(): Promise<OperatingPerformanceData> {
  const reportsPage = await getReportsPage({ limit: 200, offset: 0, days: 200 });
  const reports = reportsPage.items
    .filter((report) => report.total_value > 1000)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (reports.length === 0) {
    throw new Error('운영 성과를 계산할 일일 리포트가 없습니다.');
  }

  const latest = reports[reports.length - 1];
  const [detail, tradesPage] = await Promise.all([
    getReportDetail(latest.date),
    getRealizedTradePage({ limit: 200, offset: 0, market: 'US', scope: 'paper' }),
  ]);

  const equityCurve = reports.map((report) => ({
    date: report.date,
    value: Number(report.total_value.toFixed(2)),
  }));
  const drawdownSeries = buildDrawdownSeries(equityCurve);
  const monthlyReturns = buildMonthlyReturns(equityCurve);
  const trades = tradesPage.items;
  const realizedProfit = trades.reduce(
    (sum, trade) => sum + (trade.profit_amount ?? 0),
    0
  );
  const winningTrades = trades.filter((trade) => trade.profit_percent > 0).length;
  const unrealizedProfit = detail.positions.reduce(
    (sum, position) => sum + (position.unrealized_pl ?? 0),
    0
  );

  return {
    range: {
      start: reports[0].date,
      end: latest.date,
      days: reports.length,
    },
    summary: {
      totalValue: latest.total_value,
      cash: detail.account.cash,
      equity: detail.account.total_value - detail.account.cash,
      totalReturn: pctChange(reports[0].total_value, latest.total_value),
      latestDailyReturn: latest.daily_return,
      maxDrawdown: Math.abs(Math.min(...drawdownSeries.map((point) => point.drawdown))),
      realizedProfit,
      unrealizedProfit,
      winRate: trades.length ? (winningTrades / trades.length) * 100 : 0,
      tradeCount: trades.length,
      openPositions: detail.positions.length,
    },
    tradeBreakdown: buildTradeBreakdown(trades),
    equityCurve,
    drawdownSeries,
    monthlyReturns,
    positions: detail.positions,
    trades: trades.slice(0, 10),
  };
}

function pctChange(start: number, end: number): number {
  if (!start) return 0;
  return ((end - start) / start) * 100;
}

function buildDrawdownSeries(curve: EquityPoint[]): DrawdownPoint[] {
  let peak = 0;
  return curve.map((point) => {
    peak = Math.max(peak, point.value);
    const drawdown = peak ? ((point.value - peak) / peak) * 100 : 0;
    return { date: point.date, drawdown: Number(drawdown.toFixed(2)) };
  });
}

function buildMonthlyReturns(curve: EquityPoint[]): MonthlyReturn[] {
  const months = new Map<string, { start: number; end: number }>();
  curve.forEach((point) => {
    const month = point.date.slice(0, 7);
    const current = months.get(month);
    months.set(month, current ? { start: current.start, end: point.value } : {
      start: point.value,
      end: point.value,
    });
  });

  return Array.from(months.entries()).map(([month, values]) => ({
    month,
    return_pct: Number(pctChange(values.start, values.end).toFixed(2)),
  }));
}

function buildTradeBreakdown(trades: RealizedTrade[]) {
  const map = new Map<string, { type: string; count: number; profit: number }>();
  trades.forEach((trade) => {
    const current = map.get(trade.trade_type) ?? {
      type: trade.trade_type,
      count: 0,
      profit: 0,
    };
    current.count += 1;
    current.profit += trade.profit_amount ?? 0;
    map.set(trade.trade_type, current);
  });
  return Array.from(map.values());
}
